import React, { ReactElement, memo, useCallback, useMemo } from "react";

// types
import { AddressInfo, UserAddress } from "@swiggy-private/cart-types";

// components
import { ChevronRightIcon } from "@cart-ui/components/address-widget-top/components/icons";
import { AriaHidden, getAddressInfo } from "@swiggy-private/core-ui";

//test-ids
import { SELECT_ADDRESS_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { getAddressAnnotation, getDeliveryIcon, hasInvalidAddressCategory } from "@cart-ui/helpers/CartFooter";

import styles from "../styles/AddressList.scss";
interface AddressProps {
    address: UserAddress | AddressInfo;
    addressList: AddressInfo[];
    selectAddress: (selectedAddress: AddressInfo, addressList: AddressInfo[]) => void;
}

const ACCESSIBILITY_TEXTS = {
    SLA: "Estimated time to delivery at this address is",
    CLICK_TO_SELECT: "Click to select the address",
};

function Icon({
    category,
}: {
    category?: number;
}): ReactElement {
    const IconElement = getDeliveryIcon(category);

    return (
        <div data-testid={`${SELECT_ADDRESS_TEST_IDS.SELECT_ADDRESS_LIST_ADDRESS_ICON}_${category}`}>
            <IconElement />
        </div>
    );
}

export const Address = memo(function Address(props: AddressProps): ReactElement {
    const { address, addressList, selectAddress } = props;

    const addressCategory = useMemo(() => {
        if (hasInvalidAddressCategory(address as AddressInfo)) {
            return (address as AddressInfo).addressCategory;
        }
        return (address as AddressInfo)?.userIntentAddressCategory;
    }, [address]);

    const addressAnnotation = useMemo(() => {
        if (hasInvalidAddressCategory(address as AddressInfo)) {
            return getAddressAnnotation(address, addressCategory);
        }
        return address?.annotation || getAddressAnnotation(address, addressCategory);
    }, [address, addressCategory]);
    const onAddressSelect = useCallback(() => {
        selectAddress(address as AddressInfo, addressList);
    }, [address, addressList, selectAddress]);

    const addressInfo = useMemo(() => getAddressInfo(address), [address]);

    const accessibilityText = useMemo(() => {
        let text = `${addressAnnotation} ${addressInfo}`;

        // TODO: remove after address selector XP is concluded
        if ("sla_message" in address) {
            text += ` ${ACCESSIBILITY_TEXTS.SLA} ${address.sla_message}`;
        }

        text += ` ${ACCESSIBILITY_TEXTS.CLICK_TO_SELECT}`;
        return text;
    }, [address, addressInfo, addressAnnotation]);

    return (
        <div
            key={address.id}
            className={styles.addressListContentItem}
            onClick={onAddressSelect}
            aria-label={accessibilityText}
            role="button"
            data-testid={`${SELECT_ADDRESS_TEST_IDS.SELECT_ADDRESS_LIST_ADDRESS}_${address.id}`}
        >
            <div className={styles.addressListContentItemIconWrapper}>
                <Icon category={addressCategory} />
            </div>
            <AriaHidden as="div" className={styles.addressListContentItemText}>
                <div
                    className={styles.addressListContentItemAnnotation}
                    data-testid={SELECT_ADDRESS_TEST_IDS.SELECT_ADDRESS_LIST_ADDRESS_ANNOTATION}
                >
                    {addressAnnotation}
                </div>
                <div
                    className={styles.addressListContentItemAddress}
                    data-testid={SELECT_ADDRESS_TEST_IDS.SELECT_ADDRESS_LIST_ADDRESS_LOCATION}
                >
                    {addressInfo}
                </div>
            </AriaHidden>
            <ChevronRightIcon />
        </div>
    );
});
